import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../../server";
import {
  HiOutlineCalendar,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";

const AdminServiceManagement = () => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    serviceName: "",
    description: "",
    category: "wellness_workshop",
    price: "",
    isFree: false,
    maxParticipants: "",
    venue: "",
    address: "",
    city: "",
    isOnline: false,
    eventDate: "",
    eventEndDate: "",
    status: "draft",
  });

  const categories = [
    "wellness_workshop",
    "yoga_session",
    "spiritual_talk",
    "meditation_retreat",
    "positive_vibes_meetup",
    "product_launch_event",
    "other",
  ];

  const fetchServices = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(
        `${server}/service/admin-all-services?status=${statusFilter}&page=${page}`,
        { withCredentials: true }
      );
      setServices(data.services);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch services");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [statusFilter, page]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price: Number(formData.price) || 0,
        maxParticipants: Number(formData.maxParticipants) || null,
        location: {
          venue: formData.venue,
          address: formData.address,
          city: formData.city,
          isOnline: formData.isOnline,
        },
      };

      if (editingService) {
        await axios.put(
          `${server}/service/update-service/${editingService._id}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Service updated");
      } else {
        await axios.post(`${server}/service/create-service`, payload, {
          withCredentials: true,
        });
        toast.success("Service created");
      }
      setShowModal(false);
      setEditingService(null);
      resetForm();
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this service?")) return;
    try {
      await axios.delete(`${server}/service/delete-service/${id}`, {
        withCredentials: true,
      });
      toast.success("Service deleted");
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  const resetForm = () => {
    setFormData({
      serviceName: "",
      description: "",
      category: "wellness_workshop",
      price: "",
      isFree: false,
      maxParticipants: "",
      venue: "",
      address: "",
      city: "",
      isOnline: false,
      eventDate: "",
      eventEndDate: "",
      status: "draft",
    });
  };

  const openEdit = (service) => {
    setEditingService(service);
    setFormData({
      serviceName: service.serviceName || "",
      description: service.description || "",
      category: service.category || "wellness_workshop",
      price: service.price || "",
      isFree: service.isFree || false,
      maxParticipants: service.maxParticipants || "",
      venue: service.location?.venue || "",
      address: service.location?.address || "",
      city: service.location?.city || "",
      isOnline: service.location?.isOnline || false,
      eventDate: service.eventDate ? new Date(service.eventDate).toISOString().slice(0, 16) : "",
      eventEndDate: service.eventEndDate ? new Date(service.eventEndDate).toISOString().slice(0, 16) : "",
      status: service.status || "draft",
    });
    setShowModal(true);
  };

  const statusBadge = (status) => {
    const colors = {
      draft: "bg-gray-100 text-gray-600",
      published: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      completed: "bg-blue-100 text-blue-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-gray-100"}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="w-full p-4 md:p-6 min-h-screen bg-secondary-50">
      <div className="mb-8 pb-6 border-b border-secondary-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-accent-600 font-semibold mb-1">Admin</p>
            <h1 className="text-3xl font-bold text-black flex items-center gap-2">
              <HiOutlineCalendar className="w-8 h-8" />
              Services & Events
            </h1>
            <p className="text-secondary-500 text-sm mt-1">Manage wellness events, workshops, and services</p>
          </div>
          <div className="flex gap-2">
            <div className="flex flex-wrap gap-1">
              {["draft", "published", "cancelled", "completed"].map((s) => (
                <button
                  key={s}
                  onClick={() => { setStatusFilter(s); setPage(1); }}
                  className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                    statusFilter === s
                      ? "bg-black text-white"
                      : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
                  }`}
                >
                  {s}
                </button>
              ))}
              <button
                onClick={() => { setStatusFilter(""); setPage(1); }}
                className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${statusFilter === "" ? "bg-black text-white" : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"}`}
              >
                All
              </button>
            </div>
          <button
            onClick={() => { resetForm(); setEditingService(null); setShowModal(true); }}
            className="flex items-center gap-1 px-4 py-2 bg-black text-white text-sm font-semibold uppercase tracking-wider hover:bg-secondary-800 transition-colors"
          >
            <HiOutlinePlus className="w-4 h-4" />
            Add Service
          </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#111111]" />
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {services.length > 0 ? services.map((s) => (
            <div key={s._id} className="bg-white border border-secondary-200 shadow-sm p-4">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-semibold text-gray-900 text-sm">{s.serviceName}</h3>
                {statusBadge(s.status)}
              </div>
              <p className="text-xs text-gray-500 mb-3 line-clamp-2">{s.description}</p>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Category:</span>
                  <span className="text-gray-700 capitalize">{s.category?.replace(/_/g, " ")}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Price:</span>
                  <span className="text-gray-900 font-medium">{s.isFree ? "Free" : `₹${s.price}`}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Location:</span>
                  <span className="text-gray-700">{s.location?.isOnline ? "Online" : s.location?.city || "-"}</span>
                </div>
                {s.eventDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date:</span>
                    <span className="text-gray-700">{new Date(s.eventDate).toLocaleDateString("en-IN")}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => openEdit(s)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-100"
                >
                  <HiOutlinePencil className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(s._id)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100"
                >
                  <HiOutlineTrash className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          )) : (
            <div className="col-span-full text-center py-12 text-gray-500">No services found</div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingService ? "Edit Service" : "Create New Service"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
                <input
                  type="text"
                  value={formData.serviceName}
                  onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent resize-none"
                  rows="3"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111]"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111]"
                  >
                    {["draft", "published", "cancelled", "completed"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                    disabled={formData.isFree}
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isFree}
                      onChange={(e) => setFormData({ ...formData, isFree: e.target.checked, price: e.target.checked ? 0 : formData.price })}
                      className="w-4 h-4 text-[#111111] rounded"
                    />
                    <span className="text-sm text-gray-700">Free Event</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Participants</label>
                <input
                  type="number"
                  min="1"
                  value={formData.maxParticipants}
                  onChange={(e) => setFormData({ ...formData, maxParticipants: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Location</h4>
                <label className="flex items-center gap-2 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={formData.isOnline}
                    onChange={(e) => setFormData({ ...formData, isOnline: e.target.checked })}
                    className="w-4 h-4 text-[#111111] rounded"
                  />
                  <span className="text-sm text-gray-700">Online Event</span>
                </label>
                {!formData.isOnline && (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={formData.venue}
                      onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                      placeholder="Venue name"
                    />
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                      placeholder="Address"
                    />
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                      placeholder="City"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date & Time</label>
                  <input
                    type="datetime-local"
                    value={formData.eventDate}
                    onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date & Time</label>
                  <input
                    type="datetime-local"
                    value={formData.eventEndDate}
                    onChange={(e) => setFormData({ ...formData, eventEndDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingService(null); resetForm(); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#111111] rounded-lg hover:bg-[#1a1a1a]"
                >
                  {editingService ? "Update Service" : "Create Service"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServiceManagement;
